export const EMPTY_DELETIONS = {
    budgetIds: [],
    categoryIds: [],
    transactionIds: [],
};
export function mapServerBudget(s) {
    return {
        id: s.budgetId,
        name: s.name,
        currency: s.currency,
        icon: s.icon ?? '',
        weekStart: s.weekStart ?? 1,
        savingsEnabled: s.savingsEnabled ?? false,
        showCurrentBalance: s.showCurrentBalance ?? true,
        isArchived: s.status === 'Archived',
        storageMode: s.storageMode ?? 'CloudBound',
        version: s.version ?? 1,
        categories: [],
        transactions: [],
    };
}
export function mapServerCategory(s) {
    return {
        id: s.categoryId,
        label: s.label,
        icon: s.icon ?? '',
        type: (s.type ?? 'expense').toLowerCase(),
        version: s.version ?? 1,
    };
}
export function mapServerTransaction(s) {
    return {
        id: s.transactionId,
        date: s.date,
        planned: s.plannedAmount ?? null,
        actual: s.actualAmount ?? null,
        type: (s.type ?? 'expense').toLowerCase(),
        description: s.description ?? '',
        category: s.categoryId ?? null,
        fromSavings: s.fromSavings ?? false,
        seriesId: s.seriesId ?? undefined,
        isStart: s.isStart ?? false,
        version: s.version ?? 1,
    };
}
/**
 * Применяет серверные upsert'ы/deletion'ы к списку бюджетов и возвращает
 * новый список. Мутирует переданный массив (вызывающий передаёт свежезагруженный).
 *
 * - Бюджеты/категории/транзакции сливаются по id с учётом version (сервер
 *   перезаписывает только при большей версии → локальные несинхронизированные
 *   правки не затираются).
 * - Локальные бюджеты, которых нет в снапшоте (LocalOnly), не трогаются.
 * - Категории дедуплицируются по контенту (type+label+icon), если сервер прислал
 *   ту же категорию с новым id (последствие import) — иначе появлялся дубль-близнец.
 */
export function applyRemoteToBudgets(budgets, upserts, deletions) {
    // Apply budget upserts
    for (const remoteBudget of upserts.budgets) {
        const remoteId = remoteBudget.budgetId;
        const remoteVersion = remoteBudget.version ?? 0;
        const idx = budgets.findIndex((b) => b.id === remoteId);
        if (idx >= 0) {
            if (remoteVersion > (budgets[idx].version ?? 0)) {
                budgets[idx] = {
                    ...budgets[idx],
                    ...mapServerBudget(remoteBudget),
                    categories: budgets[idx].categories,
                    transactions: budgets[idx].transactions,
                };
            }
        }
        else {
            budgets.push(mapServerBudget(remoteBudget));
        }
    }
    // Apply category upserts
    for (const remoteCat of upserts.categories) {
        const budgetId = remoteCat.budgetId;
        const remoteVersion = remoteCat.version ?? 0;
        const budget = budgets.find((b) => b.id === budgetId);
        if (!budget)
            continue;
        const catIdx = budget.categories.findIndex((c) => c.id === remoteCat.categoryId);
        const mapped = mapServerCategory(remoteCat);
        if (catIdx >= 0) {
            if (remoteVersion > (budget.categories[catIdx].version ?? 0)) {
                budget.categories[catIdx] = mapped;
            }
        }
        else {
            // Нет совпадения по id. Проверяем дубликат по контенту: import оставляет
            // локальную DEFAULT-категорию ('food'), а сервер присылает ту же категорию
            // с новым id — иначе получаем дубль-близнец.
            const dupIdx = budget.categories.findIndex((c) => c.type === mapped.type && c.label === mapped.label && c.icon === mapped.icon);
            if (dupIdx >= 0) {
                const oldId = budget.categories[dupIdx].id;
                budget.categories[dupIdx] = mapped;
                // перепривязываем локальные транзакции со старого id на серверный
                for (const t of budget.transactions) {
                    if (t.category === oldId)
                        t.category = mapped.id;
                }
            }
            else {
                budget.categories.push(mapped);
            }
        }
    }
    // Apply transaction upserts
    for (const remoteTx of upserts.transactions) {
        const budgetId = remoteTx.budgetId;
        const remoteVersion = remoteTx.version ?? 0;
        const budget = budgets.find((b) => b.id === budgetId);
        if (!budget)
            continue;
        const txIdx = budget.transactions.findIndex((t) => t.id === remoteTx.transactionId);
        const mapped = mapServerTransaction(remoteTx);
        if (txIdx >= 0) {
            if (remoteVersion > (budget.transactions[txIdx].version ?? 0)) {
                budget.transactions[txIdx] = mapped;
            }
        }
        else {
            budget.transactions.push(mapped);
        }
    }
    // Apply deletions
    for (const id of deletions.transactionIds) {
        for (const b of budgets) {
            b.transactions = b.transactions.filter((t) => t.id !== id);
        }
    }
    for (const id of deletions.categoryIds) {
        for (const b of budgets) {
            b.categories = b.categories.filter((c) => c.id !== id);
        }
    }
    const deletedBudgetSet = new Set(deletions.budgetIds);
    return budgets.filter((b) => !deletedBudgetSet.has(b.id));
}
